import { useState, useCallback } from 'react';
import { useLanguage } from '../../../i18n';
import type { TestCaseAnalysis } from '../types';

interface UseTestCaseAnalyzerReturn {
  testCaseAnalysis: TestCaseAnalysis | null;
  isAnalyzing: boolean;
  analyzeTestCaseFile: (file: File) => Promise<void>;
  analyzeTestCaseFiles: (files: File[]) => Promise<void>;
  clearAnalysis: () => void;
}

// 단일 파일 파싱 결과 타입
interface ParsedTestCaseData {
  total: number;
  passed: number;
  failed: number;
  coreTotal: number;
  corePassed: number;
  coreFailed: number;
  edgeTotal: number;
  edgePassed: number;
  edgeFailed: number;
  parseWarning?: string;
}

export function useTestCaseAnalyzer(): UseTestCaseAnalyzerReturn {
  const { lang, t } = useLanguage();
  const [testCaseAnalysis, setTestCaseAnalysis] = useState<TestCaseAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 상태 문자열 정규화 (숨은 문자/기호 제거)
  const normalizeStatusToken = useCallback((value: string): string => {
    const v = (value ?? '')
      .toString()
      .toLowerCase()
      .trim()
      // zero-width + BOM
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // normalize unicode (e.g., full-width chars)
      .normalize('NFKC')
      // keep letters/numbers only
      .replace(/[^\p{L}\p{N}]+/gu, '');
    return v;
  }, []);

  const isPassValue = useCallback(
    (value: string): boolean => {
      const v = normalizeStatusToken(value);
      return (
        v === 'pass' ||
        v === 'passed' ||
        v === 'ok' ||
        v === 'success' ||
        v === 'true' ||
        v === 'y' ||
        v === 'yes' ||
        v === '통과' ||
        v === '성공'
      );
    },
    [normalizeStatusToken]
  );

  const isFailValue = useCallback(
    (value: string): boolean => {
      const v = normalizeStatusToken(value);
      return v === 'fail' || v === 'failed' || v === 'ng' || v === 'false' || v === 'n' || v === 'no' || v === '실패';
    },
    [normalizeStatusToken]
  );

  // CSV 파싱 함수 (따옴표/콤마 포함 셀 지원)
  const parseCsvRows = useCallback((csvText: string): string[][] => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;

    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          currentCell += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          currentCell += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          currentRow.push(currentCell);
          currentCell = '';
        } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
          currentRow.push(currentCell);
          rows.push(currentRow);
          currentRow = [];
          currentCell = '';
          if (char === '\r') i++;
        } else if (char !== '\r') {
          currentCell += char;
        }
      }
    }

    if (currentCell || currentRow.length > 0) {
      currentRow.push(currentCell);
      rows.push(currentRow);
    }

    // 빈 행 제거
    return rows.filter((r) => r.some((c) => (c ?? '').trim().length > 0));
  }, []);

  // 단일 파일 파싱 함수
  const parseTestCaseFile = useCallback(
    async (file: File): Promise<ParsedTestCaseData> => {
      const text = await file.text();
      let data: ParsedTestCaseData;

        if (file.name.endsWith('.json')) {
          // JSON 파싱 (새 템플릿 형식 지원)
          const jsonData = JSON.parse(text);
          const testCases = Array.isArray(jsonData) ? jsonData : (jsonData.testCases || jsonData.testcases || jsonData.test_cases || []);

          type TestCase = {
            Category?: string;
            category?: string;
            '분류'?: string;
            Priority?: string;
            priority?: string;
            '우선순위'?: string;
            'Actual Result'?: string;
            actualResult?: string;
            actual_result?: string;
            status?: string;
            '실제 결과'?: string;
            '실제_결과'?: string;
          };

          // Category 또는 Priority로 core/edge 구분 (한국어/영어 지원)
          const getCategory = (tc: TestCase) => {
            const cat = (tc.Category || tc.category || tc['분류'] || '').toLowerCase();
            if (cat === 'core' || cat === 'edge') return cat;
            // Category가 없으면 Priority로 판단
            const priority = (tc.Priority || tc.priority || tc['우선순위'] || '').toLowerCase();
            return priority === 'high' || priority === 'medium' ? 'core' : 'edge';
          };

          const isPassed = (tc: TestCase) => {
            const result = (
              tc['Actual Result'] ||
              tc.actualResult ||
              tc.actual_result ||
              tc.status ||
              tc['실제 결과'] ||
              tc['실제_결과'] ||
              ''
            ).toLowerCase();
            return isPassValue(result);
          };

          const coreCases = testCases.filter((tc: TestCase) => getCategory(tc) === 'core');
          const edgeCases = testCases.filter((tc: TestCase) => getCategory(tc) === 'edge');

          const corePassedCount = coreCases.filter((tc: TestCase) => isPassed(tc)).length;
          const edgePassedCount = edgeCases.filter((tc: TestCase) => isPassed(tc)).length;
          const totalPassedCount = testCases.filter((tc: TestCase) => isPassed(tc)).length;

          data = {
            total: testCases.length,
            passed: totalPassedCount,
            failed: testCases.length - totalPassedCount,
            coreTotal: coreCases.length,
            corePassed: corePassedCount,
            coreFailed: coreCases.length - corePassedCount,
            edgeTotal: edgeCases.length,
            edgePassed: edgePassedCount,
            edgeFailed: edgeCases.length - edgePassedCount,
          };
        } else if (file.name.endsWith('.md')) {
          // Markdown 테이블 파싱
          const lines = text.split('\n').filter((line) => line.trim());
          
          // Markdown 테이블 행 찾기 (|로 시작하고 끝나는 행)
          const tableRows = lines.filter((line) => line.trim().startsWith('|') && line.trim().endsWith('|'));
          
          if (tableRows.length < 2) {
            // 테이블이 없으면 빈 분석 결과
            data = {
              total: 0,
              passed: 0,
              failed: 0,
              coreTotal: 0,
              corePassed: 0,
              coreFailed: 0,
              edgeTotal: 0,
              edgePassed: 0,
              edgeFailed: 0,
              parseWarning: lang === 'ko' 
                ? '⚠️ Markdown 테이블을 찾을 수 없습니다.' 
                : '⚠️ No Markdown table found.',
            };
          } else {
            // 헤더와 구분자 제외 (첫 번째 행은 헤더, 두 번째 행은 구분자 ---)
            const header = tableRows[0].split('|').map((h) => h.trim().toLowerCase()).filter((h) => h);
            const dataRows = tableRows.slice(2); // 헤더와 구분자 스킵
            
            // 컬럼 인덱스 찾기
            const findColumnIndex = (keywords: string[]) => {
              return header.findIndex((h) => keywords.some((kw) => h.includes(kw)));
            };
            
            const categoryIdx = findColumnIndex(['category', '분류', 'type', '유형', '구분']);
            const actualResultIdx = findColumnIndex(['actual result', 'actualresult', 'status', '실제 결과', '실제결과', '결과']);
            const priorityIdx = findColumnIndex(['priority', '우선순위', '우선', '중요도']);
            
            let coreTotal = 0, corePassed = 0, edgeTotal = 0, edgePassed = 0;
            let totalPassed = 0;
            
            dataRows.forEach((row) => {
              const cols = row.split('|').map((c) => c.trim()).filter((c) => c);
              
              const category = categoryIdx >= 0 ? (cols[categoryIdx] || '').toLowerCase() : '';
              const actualResult = actualResultIdx >= 0 ? (cols[actualResultIdx] || '').toLowerCase() : '';
              const priority = priorityIdx >= 0 ? (cols[priorityIdx] || '').toLowerCase() : '';
              
              const isPassed = isPassValue(actualResult);
              if (isPassed) totalPassed++;
              
              let effectiveCategory = category;
              if (!effectiveCategory && priority) {
                effectiveCategory = priority === 'high' || priority === 'medium' ? 'core' : 'edge';
              }
              
              if (effectiveCategory === 'core') {
                coreTotal++;
                if (isPassed) corePassed++;
              } else if (effectiveCategory === 'edge') {
                edgeTotal++;
                if (isPassed) edgePassed++;
              }
            });
            
            data = {
              total: dataRows.length,
              passed: totalPassed,
              failed: dataRows.length - totalPassed,
              coreTotal,
              corePassed,
              coreFailed: coreTotal - corePassed,
              edgeTotal,
              edgePassed,
              edgeFailed: edgeTotal - edgePassed,
            };
          }
        } else {
          // CSV 파싱 (새 템플릿 형식 지원 - 한국어/영어)
          const rows = parseCsvRows(text);
          const rawHeaderRow = rows[0] || [];
          const header = rawHeaderRow.map((h, idx) => {
            const normalized = (h ?? '').trim();
            // BOM 제거 (UTF-8 with BOM)
            const withoutBom = idx === 0 ? normalized.replace(/^\uFEFF/, '') : normalized;
            return withoutBom.toLowerCase();
          });

          // 유연한 헤더 매칭 함수 (부분 문자열 매칭)
          const findColumnIndex = (keywords: string[]) => {
            return header.findIndex((h) => {
              const trimmed = h.trim();
              return keywords.some((kw) => trimmed.includes(kw));
            });
          };

          // 컬럼 인덱스 찾기 (부분 매칭으로 유연하게)
          let categoryIdx = findColumnIndex(['category', '분류', 'type', '유형', '구분']);
          let actualResultIdx = findColumnIndex([
            'actual result',
            'actualresult',
            'status',
            '실제 결과',
            '실제결과',
            '실제',
          ]);
          let priorityIdx = findColumnIndex(['priority', '우선순위', '우선', '중요도']);

          let parseWarning: string | undefined;
          let recoveredActualResultCount = 0;

          // 헤더를 찾지 못한 경우 → 템플릿 기본 컬럼 순서로 폴백
          const useDefaultColumns = actualResultIdx === -1;

          if (useDefaultColumns) {
            priorityIdx = 0; // 첫 번째 컬럼 = 우선순위
            categoryIdx = 2; // 세 번째 컬럼 = 분류
            actualResultIdx = 8; // 아홉 번째 컬럼 = 실제 결과
            parseWarning =
              lang === 'ko'
                ? '⚠️ 헤더 인식 실패 - 기본 컬럼 순서로 분석합니다. 템플릿 형식을 유지해주세요.'
                : '⚠️ Header not recognized - Using default column order. Please keep template format.';
          }

          const dataRows = rows.slice(1);

          let coreTotal = 0,
            corePassed = 0,
            edgeTotal = 0,
            edgePassed = 0;
          let totalPassed = 0;

          dataRows.forEach((colsRaw) => {
            const cols = colsRaw.map((c) => (c ?? '').trim());
            // 컬럼 수가 부족하면 스킵
            if (cols.length <= Math.max(categoryIdx, actualResultIdx, priorityIdx)) {
              return;
            }

            const category = categoryIdx >= 0 ? cols[categoryIdx]?.trim().toLowerCase() : '';
            let actualResult =
              actualResultIdx >= 0 ? cols[actualResultIdx]?.trim() ?? '' : '';
            const priority = priorityIdx >= 0 ? cols[priorityIdx]?.trim().toLowerCase() : '';

            // 실제 결과 컬럼이 깨졌거나(컬럼 밀림/숨은 문자) 값이 애매하면 행 전체에서 pass/fail 토큰을 복구 시도
            const looksKnown = isPassValue(actualResult) || isFailValue(actualResult);
            if (!looksKnown) {
              const found = cols.find((c) => isPassValue(c) || isFailValue(c));
              if (found) {
                actualResult = found;
                recoveredActualResultCount++;
              }
            }

            const isPassed = isPassValue(actualResult);
            if (isPassed) totalPassed++;

            // Category가 없으면 Priority로 core/edge 구분
            let effectiveCategory = category;
            if (!effectiveCategory && priority) {
              effectiveCategory = priority === 'high' || priority === 'medium' ? 'core' : 'edge';
            }

            if (effectiveCategory === 'core') {
              coreTotal++;
              if (isPassed) corePassed++;
            } else if (effectiveCategory === 'edge') {
              edgeTotal++;
              if (isPassed) edgePassed++;
            }
          });

          const coreFailed = coreTotal - corePassed;
          const edgeFailed = edgeTotal - edgePassed;

          if (recoveredActualResultCount > 0) {
            const recoveryMsg =
              lang === 'ko'
                ? `⚠️ 일부 행에서 '실제 결과' 컬럼을 직접 읽지 못해(pass/fail 토큰 검색으로) 복구했습니다. (복구 ${recoveredActualResultCount}건)`
                : `⚠️ Recovered 'Actual Result' from pass/fail tokens in some rows. (recovered ${recoveredActualResultCount})`;
            parseWarning = parseWarning ? `${parseWarning}\n${recoveryMsg}` : recoveryMsg;
          }

          data = {
            total: dataRows.length,
            passed: totalPassed,
            failed: dataRows.length - totalPassed,
            coreTotal,
            corePassed,
            coreFailed,
            edgeTotal,
            edgePassed,
            edgeFailed,
            parseWarning,
          };
        }

        return data;
    },
    [lang]
  );

  // 상태 판정 로직
  const formatStatus = useCallback(
    (passed: number, failed: number, total: number): { text: string; type: 'verified' | 'partial' | 'failed' | 'unverified' } => {
      if (total === 0) return { text: t.qaValue.unverified, type: 'unverified' };
      if (failed === 0) return { text: t.qaValue.verified, type: 'verified' };
      if (passed === 0) return { text: t.qaValue.failed, type: 'failed' };
      const passText =
        lang === 'ko'
          ? `${passed}/${total} 통과 (${failed}건 실패)`
          : `${passed}/${total} passed (${failed} failed)`;
      return { text: passText, type: 'partial' };
    },
    [lang, t]
  );

  // ParsedTestCaseData를 TestCaseAnalysis로 변환
  const toAnalysis = useCallback(
    (data: ParsedTestCaseData, warnings: string[]): TestCaseAnalysis => {
      const coreStatusResult = formatStatus(data.corePassed, data.coreFailed, data.coreTotal);
      const edgeStatusResult = formatStatus(data.edgePassed, data.edgeFailed, data.edgeTotal);

      return {
        total: data.total,
        passed: data.passed,
        failed: data.failed,
        coreTotal: data.coreTotal,
        corePassed: data.corePassed,
        coreFailed: data.coreFailed,
        edgeTotal: data.edgeTotal,
        edgePassed: data.edgePassed,
        edgeFailed: data.edgeFailed,
        coreStatus: coreStatusResult.text,
        coreStatusType: coreStatusResult.type,
        edgeStatus: edgeStatusResult.text,
        edgeStatusType: edgeStatusResult.type,
        parseWarning: warnings.length > 0 ? warnings.join('\n') : undefined,
      };
    },
    [formatStatus]
  );

  // 단일 파일 분석 (기존 호환성 유지)
  const analyzeTestCaseFile = useCallback(
    async (file: File) => {
      setIsAnalyzing(true);
      try {
        const data = await parseTestCaseFile(file);
        const warnings = data.parseWarning ? [data.parseWarning] : [];
        setTestCaseAnalysis(toAnalysis(data, warnings));
      } catch (error) {
        console.error('Test case file parsing error:', error);
      }
      setIsAnalyzing(false);
    },
    [parseTestCaseFile, toAnalysis]
  );

  // 여러 파일 분석 (새로운 기능)
  const analyzeTestCaseFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        setTestCaseAnalysis(null);
        return;
      }

      setIsAnalyzing(true);
      try {
        // 모든 파일 파싱
        const results = await Promise.all(files.map((f) => parseTestCaseFile(f)));
        
        // 결과 합산
        const combined: ParsedTestCaseData = {
          total: 0,
          passed: 0,
          failed: 0,
          coreTotal: 0,
          corePassed: 0,
          coreFailed: 0,
          edgeTotal: 0,
          edgePassed: 0,
          edgeFailed: 0,
        };

        const warnings: string[] = [];

        results.forEach((r, idx) => {
          combined.total += r.total;
          combined.passed += r.passed;
          combined.failed += r.failed;
          combined.coreTotal += r.coreTotal;
          combined.corePassed += r.corePassed;
          combined.coreFailed += r.coreFailed;
          combined.edgeTotal += r.edgeTotal;
          combined.edgePassed += r.edgePassed;
          combined.edgeFailed += r.edgeFailed;
          
          if (r.parseWarning) {
            warnings.push(`[${files[idx].name}] ${r.parseWarning}`);
          }
        });

        setTestCaseAnalysis(toAnalysis(combined, warnings));
      } catch (error) {
        console.error('Test case files parsing error:', error);
      }
      setIsAnalyzing(false);
    },
    [parseTestCaseFile, toAnalysis]
  );

  const clearAnalysis = useCallback(() => {
    setTestCaseAnalysis(null);
  }, []);

  return {
    testCaseAnalysis,
    isAnalyzing,
    analyzeTestCaseFile,
    analyzeTestCaseFiles,
    clearAnalysis,
  };
}

